import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Bot, ArrowBigDown } from 'lucide-react';
import VideoSection from './components/VideoSection';
import ContactForm from './components/ContactForm';
import ContactPage from './components/ContactPage';
import DemoForm from './components/DemoForm';
import PortfolioSlider from './components/PortfolioSlider';
import './index.css';

const ChatWidget: React.FC = () => {
  useEffect(() => {
    window.VG_CONFIG = {
      ID: "v7l99p679blt8i6p",
      region: 'na',
      render: 'full-width',
      stylesheets: [
        "https://vg-bunny-cdn.b-cdn.net/vg_live_build/styles.css",
      ],
    };

    const script = document.createElement("script");
    script.src = "https://vg-bunny-cdn.b-cdn.net/vg_live_build/vg_bundle.js";
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div style={{ width: '100%', maxWidth: '500px', height: '500px' }} id="VG_OVERLAY_CONTAINER" className="rounded-lg shadow-lg overflow-hidden mx-auto" />
  );
};

const App: React.FC = () => {
  const sections = [
    {
      isChat: true,
      title: "Automated Customer Support",
      description: "Let AI handle 90% of your customer queries 24/7. \n\n Works on all your social platforms.\n\nFree up 40+ hours weekly.\n\nFocus on growing your business.",
      buttonText: "Get Started",
      buttonLink: "get-started"
    },
    {
      isDemo: true,
      title: "Automated Outreach Campaign", 
      description: "Reach out to 500+ potential clients daily.\n\nHighly personalized emails.\n\nWithout landing in spam. \n\nDon't believe me?",
      buttonText: "Find Out",
      buttonLink: "outreach"
    },
    {
      isPortfolio: true,
      title: "Web Development",
      description: "Stop losing customers with cluttered, ineffective websites.\n\nGet a clear, focused site that grabs attention and turns your visitors into customers.\n\nYour website should work as hard as you do.",
      buttonText: "Start Building",
      buttonLink: "websites"
    }
  ];

  const scrollToFirstSection = () => {
    const firstSection = document.getElementById('first-section');
    if (firstSection) {
      firstSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <div className="min-h-screen bg-gray-50">
              <header className="bg-[#224c5c]">
                <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-3">
                      <Bot size={24} className="text-[#0aa19a]" />
                    </div>
                    <h1 className="text-2xl font-bold text-white">CH-ISE</h1>
                  </div>
                  <nav>
                    <ul className="flex space-x-6">
                      <li>
                        <a 
                          href="/contact" 
                          className="text-white hover:text-[#0aa19a] transition-colors duration-200 font-medium"
                        >
                          Contact
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </header>

              <main>
                <div className="bg-white py-12">
                  <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                      <h2 className="text-4xl font-bold text-[#224c5c] mb-6">
                        Automate Your Business. Save 40+ hours weekly. Get more clients.
                      </h2>
                      <h2 className="text-4xl font-bold text-[#224c5c] mb-6">
                        Guaranteed.
                      </h2>
                      <button
                        onClick={scrollToFirstSection}
                        className="inline-block px-8 py-4 bg-[#0aa19a] text-white text-lg font-semibold rounded-md hover:bg-[#224c5c] transition-colors duration-200"
                      >
                        Let's go!
                      </button>
                    </div>
                  </div>
                </div>

                {sections.map((section, index) => (
                  <div 
                    key={index}
                    id={index === 0 ? 'first-section' : undefined}
                    className={`pt-40 md:pt-32 pb-24 ${index === 1 ? 'bg-white' : 'bg-[#224c5c]'}`}
                  >
                    <div className="container mx-auto px-4">
                      <div className={`flex flex-col md:flex-row items-center gap-8 ${
                        index === 1 ? 'md:flex-row-reverse' : ''
                      }`}>
                        <div className={`w-full md:w-1/2 space-y-6 ${
                          index === 1 ? 'md:pl-12' : ''
                        }`}>
                          <h3 className={`text-2xl md:text-3xl font-bold ${
                            index === 1 ? 'text-[#224c5c]' : 'text-white'
                          }`}>
                            {section.title}
                          </h3>
                          <div className={`space-y-6 ${
                            index === 1 ? 'text-gray-700' : 'text-gray-200'
                          }`}>
                            {section.description.split('\n\n').map((paragraph, i) => (
                              <p key={i} className={`text-lg md:text-xl leading-relaxed ${
                                index === 1 ? 'font-medium' : ''
                              }`}>
                                {paragraph}
                              </p>
                            ))}
                          </div>
                          <div className="pt-4">
                            <a 
                              href={section.buttonLink} 
                              className={`inline-block px-6 md:px-8 py-3 md:py-4 text-lg font-semibold rounded-md transition-colors duration-300 ${
                                index === 1 
                                  ? 'bg-[#224c5c] text-white hover:bg-[#0aa19a]' 
                                  : 'bg-[#0aa19a] text-white hover:bg-white hover:text-[#224c5c]'
                              }`}
                            >
                              {section.buttonText}
                            </a>
                          </div>
                          {(section.isPortfolio || section.isChat) && (
                            <div className="block md:hidden mt-8 text-center">
                              <p className="text-lg font-bold text-white mb-2">
                                {section.isChat ? "Try an example customer support agent" : "Website Examples"}
                              </p>
                              <ArrowBigDown size={48} className="text-white animate-bounce mx-auto" />
                            </div>
                          )}
                        </div>
                        <div className="w-full md:w-1/2 mt-8 md:mt-0">
                          {section.isDemo ? (
                            <div className="relative">
                              <div className="absolute -top-32 md:-top-24 left-1/2 transform -translate-x-1/2 flex flex-col items-center text-center">
                                <p className="text-lg md:text-xl font-bold text-[#224c5c] mb-2">Try our automated personalized emails</p>
                                <ArrowBigDown size={48} className="text-[#224c5c] animate-bounce" />
                              </div>
                              <DemoForm />
                            </div>
                          ) : section.isChat ? (
                            <div className="relative">
                              <div className="hidden md:flex absolute -top-32 md:-top-24 left-1/2 transform -translate-x-1/2 flex-col items-center text-center z-10">
                                <p className="text-lg md:text-xl font-bold text-white mb-2">Try an example customer support agent</p>
                                <ArrowBigDown size={48} className="text-white animate-bounce" />
                              </div>
                              <div className="flex justify-center px-4 md:px-0">
                                <ChatWidget />
                              </div>
                            </div>
                          ) : section.isPortfolio ? (
                            <div className="relative">
                              <div className="hidden md:flex absolute -top-32 md:-top-24 left-1/2 transform -translate-x-1/2 flex-col items-center text-center z-10">
                                <p className="text-lg md:text-xl font-bold text-white mb-2">Website Examples</p>
                                <ArrowBigDown size={48} className="text-white animate-bounce" />
                              </div>
                              <PortfolioSlider />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="bg-white py-24">
                  <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold text-[#224c5c] mb-6">
                      Not what you're looking for?
                    </h2>
                    <p className="text-lg text-gray-700 mb-6">
                      Let us automate any boring manual tasks that are holding your business back and join other businesses already using AI automation to grow faster and work smarter.
                    </p>
                    <a 
                      href="/contact" 
                      className="inline-block px-8 py-4 bg-[#0aa19a] text-white text-lg font-semibold rounded-md hover:bg-white hover:text-[#224c5c] transition-colors duration-200"
                    >
                      Book Your Free Consultation
                    </a>
                  </div>
                </div>

                
              </main>

              <footer className="bg-[#224c5c] text-white py-8">
                <div className="container mx-auto px-4">
                  <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mr-2">
                        <Bot size={20} className="text-[#224c5c]" />
                      </div>
                      <span className="font-bold">CH-ISE</span>
                    </div>
                    <div className="text-center md:text-right">
                      <p className="mb-2">&copy; {new Date().getFullYear()} CH-ISE. All rights reserved.</p>
                      <a href="mailto:info@ch-ise.co.za" className="text-white hover:text-[#0aa19a] transition-colors duration-200">
                        info@ch-ise.co.za
                      </a>
                    </div>
                  </div>
                </div>
              </footer>
            </div>
          }
        />
        <Route path="contact" element={<ContactPage />} />
        <Route
          path="get-started"
          element={<ContactForm serviceType="Automated Customer Support" />}
        />
        <Route
          path="outreach"
          element={<ContactForm serviceType="Automated Outreach Campaign" />}
        />
        <Route
          path="websites"
          element={<ContactForm serviceType="Web Development" />}
        />
        <Route path="thank-you" element={<ThankYou />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

const ThankYou: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-[#224c5c] mb-4">Thank You!</h1>
      <p className="text-xl text-gray-600 mb-8">We'll be in touch with you shortly.</p>
      <a
        href="/"
        className="inline-block px-6 py-3 bg-[#0aa19a] text-white rounded-md hover:bg-[#224c5c] transition-colors duration-200"
      >
        Back to Home
      </a>
    </div>
  </div>
);

export default App;